import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ViewerDataSource } from "./SignalViewer";

/** Lightweight selection revalidation is separate from immutable matrix reads.
 * Neither query depends on displayed time, playback ticks or smoothing settings.
 * The deprecated raw-summary loader is intentionally never called here.
 */
export function usePositionDistribution(source: ViewerDataSource, account: string, visible: boolean,
  pin: { data_version: string; profile_sha256: string } | undefined) {
  const client = useQueryClient();
  const supported = Boolean(source.loadMetricsPositionReadiness && source.loadMetricsPositionDistribution && pin);
  const base = [...source.key, "metrics-position-distribution", account, pin?.data_version, pin?.profile_sha256];
  const readiness = useQuery({
    queryKey: [...base, "readiness"],
    queryFn: async ({ signal }) => {
      const value = await source.loadMetricsPositionReadiness!(signal);
      if (signal.aborted || value.recording_ref !== source.recordingRef || (value.available &&
          (value.identity?.data_version !== pin?.data_version || value.identity?.duration_seconds !== source.durationSeconds))) throw new Error("Metrics position source changed");
      return value;
    },
    enabled: visible && supported, retry: false, staleTime: 0, gcTime: 60_000,
    refetchInterval: visible && supported ? 15_000 : false, refetchOnWindowFocus: true,
  });
  const identity = readiness.data?.available ? readiness.data.identity : null;
  const query = useQuery({
    queryKey: [...base, "matrices", identity],
    queryFn: async ({ signal }) => {
      if (!identity) throw new Error("Selected metrics unavailable");
      const value = await source.loadMetricsPositionDistribution!(identity, signal);
      if (signal.aborted || value.recording_ref !== source.recordingRef ||
          Object.entries(identity).some(([key, expected]) => value.identity[key as keyof typeof identity] !== expected)) throw new Error("Selected metrics changed");
      return value;
    },
    enabled: visible && supported && Boolean(identity) && !readiness.isError && !readiness.isFetching,
    retry: false, gcTime: 60_000, staleTime: Infinity, refetchOnWindowFocus: false,
  });
  const scope = JSON.stringify(base);
  const selected = JSON.stringify(identity);
  useEffect(() => {
    // Revocation/unavailability must discard protected arrays, not merely hide
    // them. A new selected identity also retires the old selected-only cache.
    const prefix = [...JSON.parse(scope), "matrices"];
    const denied = readiness.isError || (readiness.isSuccess && !readiness.data.available);
    const predicate = (cached: { queryKey: readonly unknown[] }) => denied || JSON.stringify(cached.queryKey.at(-1)) !== selected;
    void client.cancelQueries({ queryKey: prefix, predicate });
    client.removeQueries({ queryKey: prefix, predicate });
  }, [client, scope, selected, readiness.isError, readiness.isSuccess, readiness.data?.available]);
  useEffect(() => {
    if (!visible) void client.cancelQueries({ queryKey: JSON.parse(scope) });
  }, [client, scope, visible]);
  useEffect(() => () => {
    // Principal/source changes and unmount discard protected arrays, including
    // abandoned in-flight data. Same-account refresh retains the same scope.
    void client.cancelQueries({ queryKey: JSON.parse(scope) });
    client.removeQueries({ queryKey: JSON.parse(scope) });
  }, [client, scope]);
  // A pending background request is not a loss of verified access. Retain only
  // this exact owner/source/selected identity; a failed revalidation still clears
  // both the display and protected matrix cache above.
  const usable = visible && supported && identity && !readiness.isError;
  return { ...query, data: usable ? query.data : undefined,
    isError: readiness.isError || query.isError,
    isFetching: supported && (readiness.isFetching || (Boolean(identity) && query.isFetching)),
    isPending: supported && (readiness.isPending || (Boolean(identity) && query.isPending)),
    isSuccess: Boolean(usable && query.isSuccess),
    unavailable: !supported || (readiness.isSuccess && !readiness.data.available),
    reason: !supported ? "No authorized selected metrics source exists for this recording." : readiness.data?.reason,
    refetch: async () => {
      const refreshed = await readiness.refetch();
      // A different selected identity enables its own matrix query. Never
      // retry the closure's old selection after readiness has moved on.
      if (!refreshed.isError && refreshed.data?.available && JSON.stringify(refreshed.data.identity) === selected) await query.refetch();
    },
  };
}
